import { useId } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useDroppable } from '@dnd-kit/core';
import { cn } from '@strivelabs/ui-core/lib/utils';
import { DURATION_BASE, EASE_OUT } from '@strivelabs/ui-core/v2';
import type { WorkflowExecution } from '@/hooks/useWorkflowExecutions';
import { DATE_GROUP_LABELS, type DateGroup } from '@/utils/date';
import { ChatGroupHeader } from './ChatGroupHeader';
import { ChatRow } from './ChatRow';
import { useChatGroupFold } from './useChatGroupFold';

export type ChatGroupKey = DateGroup | 'pinned';

/** Human label per group, the pinned shelf leading the relative-date buckets. */
const GROUP_LABELS: Record<ChatGroupKey, string> = {
  pinned: 'Pinned',
  ...DATE_GROUP_LABELS,
};

/** localStorage suffix per group, stable even if a `DateGroup` id changes shape. */
const GROUP_STORAGE_KEYS: Record<ChatGroupKey, string> = {
  pinned: 'pinned',
  today: 'today',
  yesterday: 'yesterday',
  'previous-7-days': 'previous7',
  older: 'older',
};

export interface ChatGroupProps {
  group: ChatGroupKey;
  executions: WorkflowExecution[];
  workflowInstanceId: string;
  activeExecutionId?: string;
  /** A row is being dragged somewhere in the list, so the pinned shelf stays mounted as a target even when it holds nothing. */
  isDragActive: boolean;
  /** Set only for the pinned shelf: true when its own query failed, so the shelf stays visible with an inline notice instead of disappearing. */
  hasError?: boolean;
  /** The live search term, so a matched row can show which part of its title matched. */
  search?: string;
  onRename: (execution: WorkflowExecution) => void;
  onDelete: (executionId: string) => void;
  onSetPinned: (executionId: string, pinned: boolean) => void;
}

/**
 * One foldable group: the pinned shelf, or a relative-date bucket. Fold state
 * is remembered per group in localStorage; a collapsed group unmounts its rows
 * but keeps its header, which for the pinned shelf is also the drop target.
 */
export function ChatGroup({
  group,
  executions,
  workflowInstanceId,
  activeExecutionId,
  isDragActive,
  hasError = false,
  search,
  onRename,
  onDelete,
  onSetPinned,
}: ChatGroupProps) {
  const { expanded, toggle } = useChatGroupFold(GROUP_STORAGE_KEYS[group]);
  const panelId = useId();
  const reduce = useReducedMotion();
  const isPinnedGroup = group === 'pinned';
  /** Every group calls `useDroppable` for stable hook order; only the pinned instance gets a droppable id `onDragEnd` acts on. */
  const { setNodeRef, isOver } = useDroppable({ id: isPinnedGroup ? 'pinned' : `chat-group-${group}` });

  if (isPinnedGroup && executions.length === 0 && !isDragActive && !hasError) return null;

  const isShelfEmpty = executions.length === 0;
  /** The shelf keeps a target visible for the whole drag, so pinning a second conversation reads the same as pinning the first. */
  const showDropHint = isPinnedGroup && !hasError && (isDragActive || isShelfEmpty);

  return (
    <div
      ref={isPinnedGroup ? setNodeRef : undefined}
      className={cn('transition-colors duration-v2-quick', isPinnedGroup && isOver && 'rounded-v2-lg bg-v2-sidebar-accent/60')}
    >
      <ChatGroupHeader
        label={GROUP_LABELS[group]}
        expanded={expanded}
        controls={expanded ? panelId : undefined}
        onToggle={toggle}
      />
      <AnimatePresence initial={false}>
        {expanded ? (
          <motion.div
            key="body"
            initial={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
            animate={reduce ? { opacity: 1 } : { height: 'auto', opacity: 1 }}
            exit={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
            transition={reduce ? { duration: 0 } : { duration: DURATION_BASE, ease: EASE_OUT }}
            className="overflow-hidden"
          >
            <ul id={panelId} className="flex flex-col">
              {isPinnedGroup && hasError && isShelfEmpty ? (
                <li
                  role="status"
                  className="mx-1 flex min-h-[26px] items-center rounded-v2-lg px-2 text-[13px] text-v2-sidebar-foreground/40"
                >
                  Couldn&apos;t load pinned chats
                </li>
              ) : null}

              {executions.map(execution => (
                <ChatRow
                  key={execution.id}
                  execution={execution}
                  workflowInstanceId={workflowInstanceId}
                  isActive={String(execution.id) === activeExecutionId}
                  search={search}
                  onRename={() => onRename(execution)}
                  onDelete={() => onDelete(String(execution.id))}
                  onSetPinned={pinned => onSetPinned(String(execution.id), pinned)}
                />
              ))}

              {showDropHint ? (
                <li
                  aria-hidden
                  className={cn(
                    'mx-1 flex items-center justify-center rounded-v2-lg px-2 text-[13px]',
                    'border border-dashed border-v2-sidebar-foreground/25 text-v2-sidebar-foreground/40',
                    isShelfEmpty ? 'min-h-[52px]' : 'mt-1 min-h-[34px]',
                  )}
                >
                  Drop to pin
                </li>
              ) : null}
            </ul>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
